/*
 * Client telemetry (SHER-323 traceability + SHER-320 telemetry).
 *
 * Stamps every API request with traceability headers (x-correlation-id,
 * x-app-version, x-app-gitsha, x-tenant), classifies each request's outcome,
 * and ships a compact, PII-free batch to the API via navigator.sendBeacon.
 *
 * Hard rule: instrumentation must NEVER break the actual request flow. Every
 * entry point is wrapped in try/catch and degrades to a no-op on any error.
 *
 * The pure helpers (classifyOutcome, normalizeRouteTemplate, semverLt,
 * buildBeaconPayload) take all their inputs as arguments so they can be unit
 * tested without touching import.meta / build-time globals.
 */

import {
    apiBaseUrl as safeApiBaseUrl,
    appVersion as safeAppVersion,
    appGitSha as safeAppGitSha,
    tenant as safeTenant,
    jwt as safeJwt,
    researcherUuid as safeResearcherUuid,
    safeRandomUUID,
} from './identity';
import { addBreadcrumb, flush as flushBreadcrumbs } from './breadcrumbs';

// ---------------------------------------------------------------------------
// Pure helpers (no side effects, no build-time globals)
// ---------------------------------------------------------------------------

const SLOW_THRESHOLD_MS = 2000;

/**
 * Outcome classification (frozen contract):
 *   - axios error with no `.response` -> "network_error"
 *   - axios error with a `.response`   -> "http_error"
 *   - success with clientDurationMs > 2000 -> "slow"
 *   - otherwise -> "ok"
 */
function classifyOutcome({ isError, hasResponse, clientDurationMs } = {}) {
    if (isError) {
        return hasResponse ? 'http_error' : 'network_error';
    }
    if (typeof clientDurationMs === 'number' && clientDurationMs > SLOW_THRESHOLD_MS) {
        return 'slow';
    }
    return 'ok';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGITS_RE = /^\d+$/;
// Path segments that are large numbers / timestamps (>= 6 digits anywhere)
// are treated as identifiers even if not strictly all-digit.
const BIG_NUMBER_RE = /\d{6,}/;
const QS_KEEP_KEYS = new Set(['limit', 'offset', 'slim']);

/**
 * Normalize a request URL/path into a low-cardinality route template with NO
 * patient PII. UUIDs, all-digit and large-number/timestamp segments become
 * `:id`; the query string is dropped except for limit/offset/slim.
 */
function normalizeRouteTemplate(rawUrl) {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    try {
        // Strip protocol+host if present so we only template the path.
        let path = rawUrl;
        let query = '';
        const qIdx = path.indexOf('?');
        if (qIdx >= 0) {
            query = path.slice(qIdx + 1);
            path = path.slice(0, qIdx);
        }
        // Drop scheme + host (handles absolute URLs without needing a base).
        path = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
        if (!path.startsWith('/')) path = '/' + path;

        const segments = path.split('/').map((seg) => {
            if (!seg) return seg;
            const decoded = (() => {
                try { return decodeURIComponent(seg); } catch (_) { return seg; }
            })();
            if (UUID_RE.test(decoded)) return ':id';
            if (ALL_DIGITS_RE.test(decoded)) return ':id';
            if (BIG_NUMBER_RE.test(decoded)) return ':id';
            return seg;
        });
        let template = segments.join('/') || '/';

        if (query) {
            const kept = [];
            for (const pair of query.split('&')) {
                const eq = pair.indexOf('=');
                const key = eq >= 0 ? pair.slice(0, eq) : pair;
                if (QS_KEEP_KEYS.has(key)) kept.push(pair);
            }
            if (kept.length) template = `${template}?${kept.join('&')}`;
        }
        return template;
    } catch (_) {
        return '[unparseable]';
    }
}

/** Parse "X.Y.Z" (ignoring any pre-release/build suffix) into [x,y,z]. */
function parseSemver(v) {
    if (typeof v !== 'string') return [0, 0, 0];
    const core = v.trim().replace(/^v/i, '').split(/[-+]/)[0];
    const parts = core.split('.').map((p) => parseInt(p, 10));
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

/** True if version `a` is strictly lower than version `b` (semver core). */
function semverLt(a, b) {
    const pa = parseSemver(a);
    const pb = parseSemver(b);
    for (let i = 0; i < 3; i++) {
        if (pa[i] < pb[i]) return true;
        if (pa[i] > pb[i]) return false;
    }
    return false;
}

/**
 * Build the beacon payload (frozen contract shape). Pure: takes identity + the
 * event list. Never embeds patient identifiers/values.
 */
function buildBeaconPayload({ sessionId, appVersion, appGitSha, tenant, researcherUuid, jwt, events } = {}) {
    return {
        sessionId: sessionId ?? null,
        appVersion: appVersion ?? null,
        appGitSha: appGitSha ?? null,
        tenant: tenant ?? null,
        researcherUuid: researcherUuid ?? null,
        jwt: jwt ?? null,
        events: Array.isArray(events) ? events : [],
    };
}

/** SHER-547: stay under the API /telemetry/client 32kb parser limit. */
const TELEMETRY_MAX_BODY_BYTES = 28 * 1024;

function beaconPayloadByteSize(payload) {
    try {
        const json = JSON.stringify(payload);
        if (typeof TextEncoder !== 'undefined') {
            return new TextEncoder().encode(json).length;
        }
        return Buffer.byteLength(json, 'utf8');
    } catch (_) {
        return Number.MAX_SAFE_INTEGER;
    }
}

/**
 * Drop oldest events until the serialized beacon payload fits the byte budget.
 * Pure helper — exported for regression tests.
 */
function trimEventsToByteBudget(events, metadata, maxBytes = TELEMETRY_MAX_BODY_BYTES) {
    if (!Array.isArray(events) || events.length === 0) return [];
    let batch = events;
    while (batch.length > 0) {
        const payload = buildBeaconPayload({ ...metadata, events: batch });
        if (beaconPayloadByteSize(payload) <= maxBytes) {
            return batch;
        }
        const drop = Math.max(1, Math.ceil(batch.length * 0.1));
        batch = batch.slice(drop);
    }
    return [];
}

// ---------------------------------------------------------------------------
// Runtime: identity sourcing + buffer + flush
// ---------------------------------------------------------------------------

const FLUSH_INTERVAL_MS = 15 * 1000;
// Match API MAX_EVENTS (telemetry.controller.ts) so batches aren't rejected server-side.
const MAX_EVENTS = 50;

const tstate = {
    buffer: [],
    sessionId: null,
    started: false,
    timer: null,
};

function getOrCreateSessionId() {
    if (tstate.sessionId) return tstate.sessionId;
    try {
        const existing = window.sessionStorage?.getItem('sherwoodTelemetrySessionId');
        if (existing) { tstate.sessionId = existing; return existing; }
    } catch (_) { /* ignore */ }
    const fresh = safeRandomUUID();
    try { window.sessionStorage?.setItem('sherwoodTelemetrySessionId', fresh); } catch (_) { /* ignore */ }
    tstate.sessionId = fresh;
    return fresh;
}

/** uuid v4 for the per-request x-correlation-id header. */
function newCorrelationId() {
    return safeRandomUUID();
}

/** Record one request's telemetry event. Never throws. */
function recordEvent({ correlationId, url, method, status, isError, hasResponse, clientDurationMs, apiVersion, apiGitSha } = {}) {
    try {
        const outcome = classifyOutcome({ isError, hasResponse, clientDurationMs });
        const isNetworkError = outcome === 'network_error';
        tstate.buffer.push({
            correlationId: correlationId ?? null,
            routeTemplate: normalizeRouteTemplate(url),
            method: (method || 'get').toUpperCase(),
            status: isNetworkError ? null : (typeof status === 'number' ? status : null),
            outcome,
            clientDurationMs: typeof clientDurationMs === 'number' ? clientDurationMs : null,
            apiVersion: isNetworkError ? null : (apiVersion ?? null),
            apiGitSha: isNetworkError ? null : (apiGitSha ?? null),
            ts: new Date().toISOString(),
        });
        if (tstate.buffer.length > MAX_EVENTS) {
            tstate.buffer.splice(0, tstate.buffer.length - MAX_EVENTS);
        }
    } catch (_) { /* never break requests */ }
}

/** Drain the buffer to the API via sendBeacon. Returns true if dispatched. */
function flushTelemetry() {
    try {
        if (!tstate.buffer.length) return false;
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
            // No beacon support — drop the batch rather than risk a hanging fetch.
            tstate.buffer = [];
            return false;
        }
        const events = tstate.buffer;
        tstate.buffer = [];
        const metadata = {
            sessionId: getOrCreateSessionId(),
            appVersion: safeAppVersion(),
            appGitSha: safeAppGitSha(),
            tenant: safeTenant(),
            researcherUuid: safeResearcherUuid(),
            jwt: safeJwt(),
        };
        const boundedEvents = trimEventsToByteBudget(events, metadata);
        if (!boundedEvents.length) {
            return false;
        }
        const payload = buildBeaconPayload({ ...metadata, events: boundedEvents });
        const url = `${safeApiBaseUrl()}/telemetry/client`;
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = navigator.sendBeacon(url, blob);
        if (!ok) {
            // Re-queue so a later flush can retry, bounded by MAX_EVENTS.
            tstate.buffer = events.concat(tstate.buffer).slice(-MAX_EVENTS);
        }
        return ok;
    } catch (_) {
        return false;
    }
}

/** Start the periodic + lifecycle flush hooks. Idempotent. */
function startTelemetry() {
    try {
        if (tstate.started) return;
        tstate.started = true;
        getOrCreateSessionId();
        if (typeof window !== 'undefined') {
            const onHide = () => { flushTelemetry(); };
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') flushTelemetry();
            });
            window.addEventListener('pagehide', onHide);
        }
        tstate.timer = setInterval(() => { flushTelemetry(); }, FLUSH_INTERVAL_MS);
        // Don't keep the event loop alive in node-like envs.
        if (tstate.timer && typeof tstate.timer.unref === 'function') tstate.timer.unref();
    } catch (_) { /* never break app */ }
}

/**
 * Install the request/response/error telemetry interceptors on an axios-like
 * instance (the shared utils/axios instance or the global axios default).
 *
 * Single-sources the traceability header contract (x-correlation-id,
 * x-app-version, x-app-gitsha, x-tenant) so the two transports can't drift.
 *
 * ONE uuid is generated per request and used as BOTH the x-correlation-id
 * header and the id fed to telemetry + breadcrumbs, so a request's breadcrumb,
 * its telemetry event, and the server-side log all share the same id and can
 * be joined. (Breadcrumbs keep their own session-scoped id for grouping, but
 * the per-request correlation id is this single shared uuid.)
 *
 * With `withBreadcrumbs: true` the interceptors also emit api breadcrumbs and
 * fire the notable-event flushes (`5xx`, `network-error`). Everything is
 * wrapped in try/catch — instrumentation must never break a request.
 */
function installTelemetryInterceptors(axiosLike, { withBreadcrumbs = false } = {}) {
    axiosLike.interceptors.request.use((config) => {
        try {
            const correlationId = newCorrelationId();
            config.headers = config.headers || {};
            config.headers['x-correlation-id'] = correlationId;
            config.headers['x-app-version'] = (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev');
            config.headers['x-app-gitsha'] = (typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : 'dev');
            config.headers['x-tenant'] = safeTenant();
            config.metadata = { ...(config.metadata || {}), startMs: Date.now(), correlationId };
            if (withBreadcrumbs) {
                addBreadcrumb('api', 'request', {
                    url: config.url,
                    method: (config.method || 'get').toUpperCase(),
                    correlationId,
                });
            }
        } catch (_) { /* never break requests */ }
        return config;
    });

    axiosLike.interceptors.response.use(
        (response) => {
            try {
                const startMs = response.config?.metadata?.startMs;
                const correlationId = response.config?.metadata?.correlationId;
                const durationMs = startMs ? Date.now() - startMs : undefined;
                const status = response.status;
                if (withBreadcrumbs) {
                    addBreadcrumb('api', 'response', {
                        url: response.config?.url,
                        method: (response.config?.method || 'get').toUpperCase(),
                        status,
                        durationMs,
                        correlationId,
                        requestId: response.headers?.['x-request-id'],
                    });
                }
                // axios resolves here for status < 500 (validateStatus on the
                // shared instance), so a 4xx is still an http_error outcome.
                recordEvent({
                    correlationId,
                    url: response.config?.url,
                    method: response.config?.method,
                    status,
                    isError: status >= 400,
                    hasResponse: true,
                    clientDurationMs: durationMs,
                    apiVersion: response.headers?.['x-api-version'],
                    apiGitSha: response.headers?.['x-api-gitsha'],
                });
                if (withBreadcrumbs && status >= 500) flushBreadcrumbs('5xx');
            } catch (_) { /* ignore */ }
            return response;
        },
        (error) => {
            try {
                const config = error?.config;
                const startMs = config?.metadata?.startMs;
                const correlationId = config?.metadata?.correlationId;
                const durationMs = startMs ? Date.now() - startMs : undefined;
                const hasResponse = !!error?.response;
                if (withBreadcrumbs) {
                    addBreadcrumb('api', 'network-error', {
                        url: config?.url,
                        method: (config?.method || 'get').toUpperCase(),
                        errorName: error?.name,
                        errorMessage: error?.message,
                        code: error?.code,
                        durationMs,
                        correlationId,
                    });
                }
                recordEvent({
                    correlationId,
                    url: config?.url,
                    method: config?.method,
                    status: error?.response?.status,
                    isError: true,
                    hasResponse,
                    clientDurationMs: durationMs,
                    apiVersion: error?.response?.headers?.['x-api-version'],
                    apiGitSha: error?.response?.headers?.['x-api-gitsha'],
                });
                if (withBreadcrumbs) flushBreadcrumbs('network-error');
            } catch (_) { /* ignore */ }
            return Promise.reject(error);
        }
    );
}

export {
    // pure
    classifyOutcome,
    normalizeRouteTemplate,
    semverLt,
    parseSemver,
    buildBeaconPayload,
    trimEventsToByteBudget,
    TELEMETRY_MAX_BODY_BYTES,
    // runtime
    installTelemetryInterceptors,
    newCorrelationId,
    recordEvent,
    flushTelemetry,
    startTelemetry,
    getOrCreateSessionId,
    safeTenant,
    safeResearcherUuid,
    SLOW_THRESHOLD_MS,
};
